import React, { useEffect, useRef, useState } from "react";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import img1 from "../../Image/Profile.png";
import formatDateTime from "../../components/formatDateTime";
import generateCaptcha from "../../components/captcha";
import MainButton from "../../components/ul/MainButton";
import NotFound from "../../components/ul/NotFound";
import getISTDate from "../../components/getISTDate";
import toIST from "../../components/toIST";
import {
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuClock,
  LuCoins,
  LuCopy,
  LuDot,
  LuSearch,
  LuTrash2,
  LuWallet,
} from "react-icons/lu";

const STATUS_TABS = [
  ["pending", "Pending"],
  ["approved", "Approved"],
  ["all", "All"],
];

const DATE_RANGES = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["thisWeek", "This Week"],
  ["thisMonth", "This Month"],
  ["last6Months", "Last 6 Months"],
  ["thisYear", "This Year"],
];

const STATUS_QUERY = { pending: "false", approved: "true", all: "" };

const METHOD_BADGE = {
  Upi: "bg-teal-500/15 text-brand-teal",
  Paypal: "bg-blue-500/15 text-blue-600",
  Binance: "bg-amber-500/15 text-amber-600",
  Bank: "bg-emerald-500/15 text-emerald-600",
};

// Where to send the money, per method. Bank requests only exist from the old backend.
const payoutLines = (w) => {
  switch (w.u_type) {
    case "Upi":
      return [["UPI ID", w.u_Upi]];
    case "Paypal":
      return [["PayPal email", w.u_email]];
    case "Binance":
      return [["Binance ID", w.u_binance]];
    case "Bank":
      return [
        ["Bank", w.u_Bank_Name],
        ["Account holder", w.u_Bank_AHN],
        ["Account no.", w.u_Bank_AC],
        ["IFSC", w.u_Bank_IFSC],
      ];
    default:
      return [];
  }
};

const LIMIT = 50;

const Withdraw = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [statusTab, setStatusTab] = useState("pending");
  const [selectState, setSelectState] = useState("today");
  const [startDate, setStartDate] = useState(getISTDate());
  const [endDate, setEndDate] = useState(getISTDate());
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [selected, setSelected] = useState([]);

  // Drops answers that arrive after a newer request was sent (see User.jsx).
  const requestIdRef = useRef(0);

  const fetchData = async () => {
    const requestId = ++requestIdRef.current;
    setLoader(true);
    try {
      const resp = await Calling.GetUserWithdraw({
        page: currentPage,
        limit: LIMIT,
        q: search.trim(),
        status: STATUS_QUERY[statusTab],
        startDate,
        endDate,
      });
      if (requestId !== requestIdRef.current) return;
      if (resp?.status) {
        setData(resp?.data || []);
        setTotalPages(resp?.totalPage || 1);
        setTotalRecord(resp?.totalRecord || 0);
      } else {
        toast.error(resp?.message || "Failed to load withdrawals");
      }
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
    } finally {
      if (requestId === requestIdRef.current) setLoader(false);
    }
  };

  useEffect(() => {
    setSelected([]);
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, statusTab, startDate, endDate]);

  // Debounced search; the first run is the load above.
  const searchMountedRef = useRef(false);
  useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }
    const delay = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line
  }, [search]);

  const handleDateRangeFilter = (range) => {
    const today = new Date();
    let start, end;
    switch (range) {
      case "today":
        start = end = toIST(today);
        break;
      case "yesterday": {
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        start = end = toIST(y);
        break;
      }
      case "thisWeek": {
        const first = new Date(today);
        first.setDate(today.getDate() - today.getDay());
        const last = new Date(first);
        last.setDate(first.getDate() + 6);
        start = toIST(first);
        end = toIST(last);
        break;
      }
      case "thisMonth":
        start = toIST(new Date(today.getFullYear(), today.getMonth(), 1));
        end = toIST(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      case "last6Months":
        start = toIST(new Date(today.getFullYear(), today.getMonth() - 5, 1));
        end = toIST(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      case "thisYear":
        start = toIST(new Date(today.getFullYear(), 0, 1));
        end = toIST(new Date(today.getFullYear(), 11, 31));
        break;
      default:
        start = end = "";
    }
    setSelectState(range);
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  const handleFormDate = (e) => {
    if (e.target.name === "startDate") setStartDate(e.target.value);
    else setEndDate(e.target.value);
    setSelectState("range");
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setCurrentPage(1);
    setSearch(e.target.value);
  };

  const handleStatusTab = (tab) => {
    setStatusTab(tab);
    setCurrentPage(1);
  };

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const allSelected = data.length > 0 && selected.length === data.length;
  const toggleSelectAll = () =>
    setSelected(allSelected ? [] : data.map((w) => w._id));

  const handleApprove = async (ids) => {
    const pending = ids.filter((id) => data.find((w) => w._id === id && !w.status));
    if (!pending.length) return toast.info("Nothing pending in the selection");
    if (!generateCaptcha("You are about to approve withdrawal(s).")) return;
    setLoader(true);
    try {
      const resp = await Calling.AcceptUserWithdraw({ id: pending });
      if (resp?.status) {
        toast.success(resp?.message || "Approved");
        setSelected([]);
        fetchData();
      } else toast.error(resp?.message || "Failed to approve");
    } catch (error) {
      console.error("Approve error:", error);
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async (ids) => {
    if (!ids.length) return;
    if (!generateCaptcha("You are about to delete withdrawal request(s).")) return;
    setLoader(true);
    try {
      const resp = await Calling.DeleteUserWithdraw({ id: ids });
      if (resp?.status) {
        toast.success(resp?.message || "Deleted");
        setSelected([]);
        fetchData();
      } else toast.error(resp?.message || "Failed to delete");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const money = (w) => `${w.u_amount_type === "rs" ? "₹" : "$"} ${w.u_Amount ?? 0}`;

  const dateInputCls = (active) =>
    `ui-input cursor-pointer rounded-xl px-3 py-2 text-sm ${
      active ? "border-teal-400 text-brand-teal" : ""
    }`;

  return (
    <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 pt-6 pb-5">
        <div className="flex items-center gap-4">
          <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
            <LuWallet className="text-2xl md:text-3xl" />
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
              Withdraw Requests
            </h1>
            <p className="text-sm text-main-subfont mt-1.5">
              Pay out and approve the users' withdrawal requests
            </p>
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map(([key, label]) => (
            <MainButton
              key={key}
              label={label}
              selectState={statusTab === key}
              onClick={() => handleStatusTab(key)}
            />
          ))}
        </div>
      </div>

      {/* Date range — pending requests are always listed whatever their date */}
      {statusTab === "pending" ? (
        <p className="pb-5 border-b border-main-border text-sm text-main-subfont">
          Showing every pending request, oldest dates included.
        </p>
      ) : (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pb-5 border-b border-main-border">
          <div className="sm:hidden w-full">
            <select
              className="ui-input w-full px-3 py-2 rounded-xl text-sm cursor-pointer"
              onChange={(e) => handleDateRangeFilter(e.target.value)}
              value={selectState}
            >
              {DATE_RANGES.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
              <option value="range">Custom Range</option>
            </select>
          </div>
          <div className="hidden sm:flex flex-wrap gap-2">
            {DATE_RANGES.map(([key, label]) => (
              <MainButton
                key={key}
                label={label}
                selectState={selectState === key}
                onClick={() => handleDateRangeFilter(key)}
              />
            ))}
          </div>
          <form className="hidden md:flex items-center gap-2">
            <input
              type="date"
              name="startDate"
              value={startDate}
              onChange={handleFormDate}
              className={dateInputCls(selectState === "range")}
            />
            <span className="text-main-subfont text-sm">to</span>
            <input
              type="date"
              name="endDate"
              value={endDate}
              onChange={handleFormDate}
              className={dateInputCls(selectState === "range")}
            />
          </form>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mt-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-main-border px-4 py-2.5 text-sm text-main-subfont">
            <LuWallet size={16} className="text-brand-teal" /> Total
            <b className="text-main-font">
              {totalRecord < 10 ? "0" + totalRecord : totalRecord}
            </b>
          </span>
          <label className="inline-flex items-center gap-2 rounded-xl bg-white border border-main-border px-4 py-2.5 text-sm text-main-subfont cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              className="accent-teal-500 w-4 h-4"
            />
            Select all
          </label>
          {selected.length > 0 && (
            <>
              <button
                onClick={() => handleApprove(selected)}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 transition-all"
              >
                <LuCheck size={16} /> Approve ({selected.length})
              </button>
              <button
                onClick={() => handleDelete(selected)}
                className="inline-flex items-center gap-2 rounded-xl bg-red-500/90 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 transition-all"
              >
                <LuTrash2 size={16} /> Delete ({selected.length})
              </button>
            </>
          )}
        </div>

        {/* Search */}
        <div className="relative w-full lg:w-[320px]">
          <LuSearch
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-main-subfont z-10"
            size={18}
          />
          <input
            type="search"
            placeholder="Search name, method, UPI / email / Binance ID"
            value={search}
            onChange={handleSearch}
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white border border-main-border text-main-font placeholder-main-subfont outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
        </div>
      </div>

      {/* Requests */}
      {loader ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white border border-main-border shadow-sm p-5 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-black/10" />
                <div className="flex-1">
                  <div className="w-28 h-3.5 bg-black/10 rounded mb-2" />
                  <div className="w-40 h-3 bg-black/5 rounded" />
                </div>
              </div>
              <div className="w-full h-16 bg-black/5 rounded-xl mt-5" />
              <div className="flex gap-2 mt-5">
                <div className="flex-1 h-9 bg-black/10 rounded-lg" />
                <div className="w-9 h-9 bg-black/10 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : data.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {data.map((w) => (
            <div
              key={w._id}
              className={`rounded-2xl bg-white border p-5 shadow-sm transition-all duration-300 ${
                selected.includes(w._id)
                  ? "border-teal-400/50 ring-1 ring-brand-teal/30"
                  : "border-main-border hover:border-brand-teal/40 hover:shadow-md"
              }`}
            >
              {/* Who */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={w?.u_info?.u_profile || img1}
                    alt={w?.u_Name || "user"}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = img1;
                    }}
                    className="w-12 h-12 rounded-full ring-1 ring-main-border object-cover shrink-0 bg-main-bg"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-main-font truncate">
                      {w.u_Name || w?.u_info?.u_name || "N/A"}
                    </p>
                    <p className="text-xs text-main-subfont truncate">
                      {w?.u_info?.u_email || "User deleted"}
                    </p>
                    {w.status ? (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 text-[11px] rounded-full bg-teal-500/15 text-brand-teal gap-1 font-medium">
                        <LuDot size={12} /> Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 text-[11px] rounded-full bg-amber-500/15 text-amber-600 gap-1 font-medium">
                        <LuDot size={12} /> Pending
                      </span>
                    )}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={selected.includes(w._id)}
                  onChange={() => toggleSelect(w._id)}
                  className="accent-teal-500 w-4 h-4 cursor-pointer mt-1 shrink-0"
                />
              </div>

              {/* What */}
              <div className="mt-4 pt-4 border-t border-main-border">
                <div className="flex items-center justify-between gap-3">
                  <span
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                      METHOD_BADGE[w.u_type] || "bg-black/5 text-main-subfont"
                    }`}
                  >
                    {w.u_type}
                  </span>
                  <span className="text-xl font-extrabold text-main-font">
                    {money(w)}
                  </span>
                </div>

                <div className="mt-3 space-y-1.5">
                  {payoutLines(w).map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-center justify-between gap-2 rounded-lg bg-main-bg px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                          {label}
                        </p>
                        <p className="text-sm font-semibold text-main-font break-all">
                          {value || "-"}
                        </p>
                      </div>
                      {value && (
                        <button
                          onClick={() => copy(value)}
                          title="Copy"
                          className="shrink-0 rounded-lg border border-main-border bg-white p-2 text-main-subfont hover:text-brand-teal hover:border-brand-teal/40 transition-all"
                        >
                          <LuCopy size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-3 mt-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                      Coins
                    </p>
                    <p className="text-sm font-semibold text-main-font mt-0.5 inline-flex items-center gap-1.5">
                      <LuCoins size={14} className="text-brand-teal" />
                      {w.u_wcoin ?? 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                      Balance after
                    </p>
                    <p className="text-sm font-semibold text-main-font mt-0.5">
                      {w.total_coin ?? 0}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                      Requested
                    </p>
                    <p className="text-xs font-medium text-main-font mt-0.5 inline-flex items-center gap-1.5">
                      <LuClock size={13} className="text-brand-teal" />
                      {w.createdAt ? formatDateTime(w.createdAt) : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                {!w.status && (
                  <button
                    onClick={() => handleApprove([w._id])}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 transition-all"
                  >
                    <LuCheck size={16} /> Approve
                  </button>
                )}
                <button
                  onClick={() => handleDelete([w._id])}
                  title="Delete request"
                  className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500/90 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500 transition-all ${
                    w.status ? "flex-1" : ""
                  }`}
                >
                  <LuTrash2 size={16} /> {w.status ? "Delete" : ""}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <NotFound title="No withdraw requests" />
        </div>
      )}

      {/* Pagination */}
      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 text-sm font-medium rounded-xl flex items-center gap-2 transition-all ${
                currentPage === 1
                  ? "bg-white text-main-subfont cursor-not-allowed border border-main-border"
                  : "bg-white border border-main-border text-main-font hover:border-teal-400/50 hover:text-brand-teal"
              }`}
            >
              <LuArrowLeft size={14} /> Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 text-sm font-medium rounded-xl flex items-center gap-2 transition-all ${
                currentPage === totalPages
                  ? "bg-white text-main-subfont cursor-not-allowed border border-main-border"
                  : "bg-white border border-main-border text-main-font hover:border-teal-400/50 hover:text-brand-teal"
              }`}
            >
              Next <LuArrowRight size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-main-subfont">
            Page
            <span className="px-3 py-1.5 bg-white border border-main-border rounded-lg font-semibold text-main-font">
              {currentPage <= 9 ? `0${currentPage}` : currentPage}
            </span>
            of
            <span className="px-3 py-1.5 bg-white border border-main-border rounded-lg font-semibold text-main-font">
              {totalPages <= 9 ? `0${totalPages}` : totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdraw;
